import { ICell, IRow, IBlockWidth, IImage, IField, Form } from '@lib-shared/editor/types';
import {
  EBlockWidth,
  EBreakpoint,
  EFieldType,
  EImageSize,
  ENodeType,
} from '@lib-shared/editor/types/enums';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentPage } from '@lib-shared/types/document-page';

export class EditorHelper {
  static readonly NANO_ID_LENGTH = 21;

  static hasHiddenContentRight({ offsetWidth, scrollWidth, scrollLeft }: HTMLElement): boolean {
    if (scrollWidth <= offsetWidth) {
      return false;
    }

    return offsetWidth + scrollLeft !== scrollWidth;
  }

  static isBlockWidth(size: EBlockWidth, width: number): boolean {
    switch (size) {
      case EBlockWidth.XXSmall:
        return width <= EBreakpoint.XXSmall;
      case EBlockWidth.XSmall:
        return width <= EBreakpoint.XSmall;
      case EBlockWidth.Small:
        return width <= EBreakpoint.Small;
      case EBlockWidth.Medium:
        return width <= EBreakpoint.Medium;
      case EBlockWidth.Large:
        return width <= EBreakpoint.Large;
      case EBlockWidth.XLarge:
        return width > EBreakpoint.Large;
    }
  }

  static getBlockWidth(width: number): IBlockWidth {
    return {
      xxs: EditorHelper.isBlockWidth(EBlockWidth.XXSmall, width),
      xs: EditorHelper.isBlockWidth(EBlockWidth.XSmall, width),
      sm: EditorHelper.isBlockWidth(EBlockWidth.Small, width),
      md: EditorHelper.isBlockWidth(EBlockWidth.Medium, width),
      lg: EditorHelper.isBlockWidth(EBlockWidth.Large, width),
      xl: EditorHelper.isBlockWidth(EBlockWidth.XLarge, width),
    };
  }

  static getBlockWidthClass(blockWidth: IBlockWidth): EBlockWidth {
    if (blockWidth?.xxs) {
      return EBlockWidth.XXSmall;
    } else if (blockWidth?.xs) {
      return EBlockWidth.XSmall;
    } else if (blockWidth?.sm) {
      return EBlockWidth.Small;
    } else if (blockWidth?.md) {
      return EBlockWidth.Medium;
    } else if (blockWidth?.lg) {
      return EBlockWidth.Large;
    } else if (blockWidth?.xl) {
      return EBlockWidth.XLarge;
    } else {
      return EBlockWidth.XXSmall;
    }
  }

  static rowContainsCoverImage({ cells }: IRow): boolean {
    const imageCoverNode = (node: IImage) =>
      node.type === ENodeType.Image && node.imageSize === EImageSize.Cover;

    return !!cells.find(({ nodes }: ICell) => !!nodes.find(imageCoverNode));
  }

  static getEditorInputFieldsWithPageNumInPage({
    editorBlock,
    pageNum,
  }: IDocumentPage): (IField & { pageNum: number })[] {
    if (!editorBlock) {
      return [];
    }
    return editorBlock.content.sections.flatMap(({ rows }) =>
      rows.flatMap(({ cells }) =>
        cells.flatMap(({ nodes }) =>
          nodes
            .filter(node => node.type === ENodeType.Form)
            .flatMap(({ fields }: Form) =>
              fields
                .filter(field => field.type === EFieldType.Input)
                .map(field => ({ ...field, pageNum }))
            )
        )
      )
    );
  }

  static getEditorInputFieldsWithPageNumInDocument({
    pages,
  }: IDocument): (IField & { pageNum: number })[] {
    return pages
      .filter(page => page.editorBlock)
      .flatMap(EditorHelper.getEditorInputFieldsWithPageNumInPage);
  }

  static getRequiredFields(document: IDocument): (IField & { pageNum: number })[] {
    return EditorHelper.getEditorInputFieldsWithPageNumInDocument(document).filter(
      field => field.inputSettings.required
    );
  }

  static IsJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }
}
